import type { CvParseQuality, ParsedCvData } from '@tabbio/types';
import { api } from './api';
import type { CvImportReview } from './cv-import-review';

export interface CvImportEvidenceEntry {
  sourceKind: 'file' | 'text' | 'provided_document';
  sourceText: string | null;
  sourceTextTruncated: boolean;
  originalFileAvailable: false;
  extracted: ParsedCvData | null;
  parsed: ParsedCvData;
  quality: CvParseQuality | null;
  capturedAt: string;
  review?: CvImportReview;
  reviewedAt?: string;
}

export interface CvImportEvidence {
  current: CvImportEvidenceEntry;
  previous: CvImportEvidenceEntry | null;
}

export function canReadCvImportEvidence(input: {
  enabled: boolean;
  cvId?: string | null;
  userId?: string | null;
  ownerId?: string | null;
}) {
  return input.enabled && Boolean(input.userId) &&
    (!input.cvId || input.cvId === 'master' || input.ownerId === input.userId);
}

export async function fetchCvImportEvidence(cvId?: string | null): Promise<CvImportEvidence | null> {
  const target = cvId && cvId !== 'master' ? encodeURIComponent(cvId) : 'master';
  const res = await api.get<CvImportEvidence | null>(`/api/cv/${target}/import-evidence`);
  if (res.error) throw new Error('Could not load import evidence');
  return res.data?.current ? res.data : null;
}

/** Only source representations can describe their original date precision. */
export function importDatePrecisionCounts(parsed: ParsedCvData) {
  const result = { year: 0, month: 0, unknown: 0 };
  const visit = (value: unknown, key = '') => {
    if (Array.isArray(value)) value.forEach((item) => visit(item));
    else if (value && typeof value === 'object') Object.entries(value).forEach(([name, item]) => visit(item, name));
    else if (/date$/i.test(key) && typeof value === 'string' && value.trim()) {
      if (/^\d{4}$/.test(value)) result.year += 1;
      else if (/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) result.month += 1;
      else if (/^(present|current|ongoing|حتى الآن|الحالي|الآن)$/i.test(value.trim())) return;
      else {
        const day = value.slice(0, 10);
        const calendarDate = new Date(`${day}T00:00:00.000Z`);
        const validDay = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])(?:T.*)?$/.test(value) &&
          Number.isFinite(calendarDate.getTime()) &&
          calendarDate.toISOString().slice(0, 10) === day && Number.isFinite(Date.parse(value));
        if (!validDay) result.unknown += 1;
      }
    }
  };
  visit(parsed);
  return result;
}
